import type { Capability, LedgerSummary, Principal } from '@taler/api-client'
import { sessionKeys } from '@taler/api-client'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef, inject, type InjectionKey, type Ref, ref } from 'vue'
import { useApi } from './api.ts'
import { retrying } from './retrying.ts'

export interface Session {
  readonly principal: ComputedRef<Principal | undefined>
  readonly ledgers: ComputedRef<readonly LedgerSummary[]>
  readonly activeLedger: ComputedRef<LedgerSummary | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  selectLedger(ledgerId: string): void
  /** Re-reads the principal and the ledger list after a failed load. */
  retry(): void
  can(capability: Capability): boolean
}

export const ACTIVE_LEDGER_KEY: InjectionKey<Ref<string | undefined>> = Symbol('taler.activeLedger')

export function createActiveLedgerRef(): Ref<string | undefined> {
  return ref<string | undefined>(undefined)
}

export function useSession(): Session {
  const api = useApi()
  const active = inject(ACTIVE_LEDGER_KEY, undefined) ?? createActiveLedgerRef()

  const principalQuery = useQuery({
    queryKey: sessionKeys.principal(),
    queryFn: ({ signal }) => api.session.principal(signal),
    retry: false,
  })
  const ledgersQuery = useQuery({
    queryKey: sessionKeys.ledgers(),
    queryFn: ({ signal }) => api.session.ledgers(signal),
    retry: false,
  })

  const ledgers = computed(() => ledgersQuery.data.value ?? [])
  const activeLedger = computed(() => resolveActiveLedger(ledgers.value, active.value))

  return {
    principal: computed(() => principalQuery.data.value),
    ledgers,
    activeLedger,
    isLoading: computed(() => principalQuery.isPending.value || ledgersQuery.isPending.value),
    error: computed(() => principalQuery.error.value ?? ledgersQuery.error.value),
    retrying: retrying(principalQuery, ledgersQuery),
    selectLedger: (ledgerId) => {
      active.value = ledgerId
    },
    retry: () => {
      void principalQuery.refetch()
      void ledgersQuery.refetch()
    },
    can: (capability) => activeLedger.value?.capabilities.includes(capability) === true,
  }
}

/** Presentation only. The server re-checks every call; this exists to avoid offering an action that will 403. */
export function hasCapability(ledger: LedgerSummary | undefined, capability: Capability): boolean {
  return ledger?.capabilities.includes(capability) === true
}

/** The one place a selected ledger id resolves to a ledger, so the guard and the switcher never disagree. */
export function resolveActiveLedger(
  ledgers: readonly LedgerSummary[],
  selectedId: string | undefined,
): LedgerSummary | undefined {
  return ledgers.find((ledger) => ledger.id === selectedId) ?? ledgers[0]
}
